import { lazy, Suspense, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  ArrowDownRight,
  ArrowUpRight,
  Box,
  Check,
  ChevronRight,
  Phone,
  Receipt,
  Truck,
  UserCog,
  Users,
  Wallet,
} from 'lucide-react'
import { useDatos } from '../store/datos'
import { useSesion } from '../store/sesion'
import { useUI } from '../store/ui'
import { Pagina } from '../components/Layout'
import { Avatar, Boton, Entrada, Tarjeta } from '../components/ui/base'
import { BotonInstalarApp } from '../components/InstalarApp'
import { IconoWhatsApp } from '../components/IconoWhatsApp'
import type { Cliente, Venta } from '../types'
import { cn } from '../lib/cn'
import {
  diasHasta,
  ESTADO_STOCK_META,
  estadoStock,
  fecha,
  fechaLarga,
  hora,
  inicioDia,
  inicioMes,
  inicioSemana,
  money,
  moneyCorto,
  num,
  pct,
  relativo,
} from '../lib/format'
import {
  desde,
  resumenInventario,
  seguimientosPendientes,
  serieDiaria,
  suma,
  variacion,
  vigentes,
} from '../lib/metricas'
import { ESTILO_WHATSAPP } from '../lib/tonos'
import { supabaseConfigurado } from '../data/supabaseAdapter'

// Los mismos "neón" que se usan en Inventario para poco stock: bordes
// que se notan de verdad, no un tono pastel más.
const BORDE_ORO_NEON = {
  borderColor: '#e8b400',
  borderWidth: 2,
  boxShadow: '0 0 0 1px rgba(232,180,0,0.35), 0 0 16px 2px rgba(232,180,0,0.6)',
}
const BORDE_ROJO_NEON = {
  borderColor: '#ff1a3c',
  borderWidth: 2,
  boxShadow: '0 0 0 1px rgba(255,26,60,0.35), 0 0 16px 2px rgba(255,26,60,0.65)',
}
// El botón "Cobrado": mismo trato neón, pero en verde —es la única
// acción positiva en un recuadro que por lo demás es todo urgencia roja.
const FONDO_VERDE_NEON = {
  backgroundColor: '#3ad14f',
  boxShadow: '0 0 0 1px rgba(58,209,79,0.4), 0 0 14px 2px rgba(58,209,79,0.65)',
}

const GraficoVentas = lazy(() => import('../components/GraficoVentas'))

/** Qué le falta a la ficha de un cliente para estar completa. Solo
 *  nombre, dirección y teléfono son obligatorios al crearlo —hay que
 *  poder cerrar una venta rápido, en terreno—, así que el resto queda
 *  pendiente y se le recuerda al vendedor después, con calma. Empresa
 *  y particular no necesitan lo mismo: a una empresa le sirve tener
 *  comuna y un contacto para coordinar despachos; a un particular, con
 *  el correo alcanza. */
function datosFaltantes(c: Cliente): string[] {
  const faltan: string[] = []
  if (c.tipo === 'empresa') {
    if (!c.comuna?.trim()) faltan.push('comuna')
    if (!c.contacto?.trim()) faltan.push('contacto principal')
    if (!c.email?.trim()) faltan.push('correo')
  } else {
    if (!c.email?.trim()) faltan.push('correo')
  }
  return faltan
}

export function Inicio() {
  const datos = useDatos()
  const marcarPagada = useDatos((s) => s.marcarPagada)
  const marcarFacturada = useDatos((s) => s.marcarFacturada)
  const usuario = useSesion((s) => s.usuario)!
  const avisar = useUI((s) => s.avisar)
  const navegar = useNavigate()
  const esAdmin = usuario.rol === 'admin'

  // Un vendedor ve su propio desempeño; el administrador ve el del negocio.
  const ventas = useMemo(
    () => (esAdmin ? datos.ventas : datos.ventas.filter((v) => v.usuarioId === usuario.id)),
    [datos.ventas, esAdmin, usuario.id],
  )

  const inv = useMemo(
    () => resumenInventario(datos.productos, datos.ajustes.stockMinimoGlobal),
    [datos.productos, datos.ajustes.stockMinimoGlobal],
  )

  // Qué del catálogo propio (no de un local de POS) hay que reponer
  // pronto —lo mismo que ya marca Inventario con "Poco stock"/"Sin
  // stock", pero visible de entrada acá, sin tener que ir a buscarlo.
  const stockCritico = useMemo(
    () =>
      datos.productos
        .filter((p) => p.activo)
        .map((p) => ({ producto: p, estado: estadoStock(p, datos.ajustes.stockMinimoGlobal) }))
        .filter((x) => x.estado !== 'disponible')
        .sort((a, b) => (a.estado === b.estado ? 0 : a.estado === 'agotado' ? -1 : 1)),
    [datos.productos, datos.ajustes.stockMinimoGlobal],
  )

  const hoy = useMemo(() => variacion(ventas, inicioDia()), [ventas])
  const semana = useMemo(() => variacion(ventas, inicioSemana()), [ventas])
  const mes = useMemo(() => variacion(ventas, inicioMes()), [ventas])
  const serie = useMemo(() => serieDiaria(ventas, 14), [ventas])

  const pendientes = useMemo(
    () => vigentes(ventas).filter((v) => v.estado !== 'entregada'),
    [ventas],
  )
  const ultimas = useMemo(() => vigentes(ventas).slice(0, 5), [ventas])

  const ventasHoy = desde(ventas, inicioDia()).length
  const saludo = new Date().getHours() < 12 ? 'Buenos días' : new Date().getHours() < 20 ? 'Buenas tardes' : 'Buenas noches'

  // Clientes a los que ya toca volver a ofrecer: cada vendedor ve los
  // suyos (los que él mismo atendió la última vez); el administrador
  // los ve todos.
  const seguimientos = useMemo(() => {
    const todos = seguimientosPendientes(datos.ventas, datos.clientes)
    return esAdmin ? todos : todos.filter((s) => s.usuarioId === usuario.id)
  }, [datos.ventas, datos.clientes, esAdmin, usuario.id])

  // Cobros vencidos: cada vendedor ve los de SUS ventas —es a quien le
  // toca ir a cobrarlas— y el administrador los ve todos, que es el
  // único que necesita la foto completa de lo que se le debe al
  // negocio. Antes le aparecían todos a todos y terminaba siendo ruido:
  // un vendedor no puede hacer nada con la deuda de un cliente que no
  // atiende él.
  const cobrosVencidos = useMemo(
    () =>
      datos.ventas
        .filter((v) => v.metodoPago === 'credito' && !v.pagado && v.estado !== 'anulada')
        .filter((v) => diasHasta(v.vencimientoPago) <= 0)
        .filter((v) => esAdmin || v.usuarioId === usuario.id)
        .sort((a, b) => (a.vencimientoPago ?? '').localeCompare(b.vencimientoPago ?? '')),
    [datos.ventas, esAdmin, usuario.id],
  )

  // Empresas y particulares se cobran distinto —una empresa suele pagar
  // contra factura, un particular por transferencia directa—, así que
  // van en dos grupos separados dentro del recuadro en vez de mezclados.
  const cobrosEmpresas = useMemo(
    () => cobrosVencidos.filter((v) => v.clienteTipo === 'empresa'),
    [cobrosVencidos],
  )
  const cobrosParticulares = useMemo(
    () => cobrosVencidos.filter((v) => v.clienteTipo === 'particular'),
    [cobrosVencidos],
  )

  // Ventas a empresa que todavía no tienen factura emitida. Solo lo ve
  // el administrador —es quien la hace en el sistema de facturación de
  // verdad, por fuera de esta app—. No importa el estado del pedido
  // (puede estar recién vendido o ya entregado): lo que manda es si ya
  // se facturó o no.
  const facturasPendientes = useMemo(
    () =>
      esAdmin
        ? datos.ventas
            .filter((v) => v.clienteTipo === 'empresa' && v.estado !== 'anulada' && !v.facturaEmitida)
            .sort((a, b) => b.fecha.localeCompare(a.fecha))
        : [],
    [datos.ventas, esAdmin],
  )

  // Fichas de cliente incompletas: el vendedor ve las suyas —los
  // clientes a los que él mismo le ha vendido—; el administrador las ve
  // todas, para pescar huecos de datos en todo el negocio.
  const clientesIncompletos = useMemo(() => {
    const propios = esAdmin
      ? datos.clientes
      : datos.clientes.filter((c) =>
          datos.ventas.some((v) => v.clienteId === c.id && v.usuarioId === usuario.id),
        )
    return propios
      .map((cliente) => ({ cliente, faltan: datosFaltantes(cliente) }))
      .filter((x) => x.faltan.length > 0)
  }, [datos.clientes, datos.ventas, esAdmin, usuario.id])

  // A qué local le está quedando poco de algo.
  //
  // Sale de las tablas de NovaBerry POS, no de una copia local: es el
  // mismo `stockMinimo` que mira el local en su propia pantalla, así
  // que el aviso aparece igual en las dos apps y no hay forma de que
  // digan cosas distintas. Solo lo pide el administrador, y en
  // segundo plano: si falla, la pantalla de inicio sigue como si nada.
  const [faltantes, setFaltantes] = useState<
    { id: string; producto: string; sucursal: string; sucursalId: string; cantidad: number }[]
  >([])

  useEffect(() => {
    if (!esAdmin || !supabaseConfigurado) return
    let cancelado = false
    void (async () => {
      try {
        const { traerStockTodo, traerSucursales, traerProductosPos } = await import('../data/pos')
        const [stock, sucursales, productos] = await Promise.all([
          traerStockTodo(),
          traerSucursales(),
          traerProductosPos(),
        ])
        if (cancelado) return
        const activas = new Map(sucursales.filter((s) => s.activa).map((s) => [s.id, s]))
        const porId = new Map(productos.map((p) => [p.id, p]))
        setFaltantes(
          stock
            .filter(
              (s) =>
                s.activo &&
                activas.has(s.sucursalId) &&
                s.stockMinimo != null &&
                s.cantidad <= s.stockMinimo,
            )
            .map((s) => ({
              id: s.id,
              producto: porId.get(s.productoId)?.nombre ?? s.productoId,
              sucursal: activas.get(s.sucursalId)!.nombre,
              sucursalId: s.sucursalId,
              cantidad: s.cantidad,
            })),
        )
      } catch {
        // POS todavía no instalado, o sin señal: no es motivo para
        // romperle la pantalla de inicio a nadie.
      }
    })()
    return () => {
      cancelado = true
    }
  }, [esAdmin])

  return (
    <Pagina>
      {/* Botón de instalar: su propia fila arriba del todo, para no
          apretar el saludo en pantallas chicas. */}
      <div className="mb-3 flex justify-end anim-aparecer">
        <BotonInstalarApp />
      </div>

      {/* Saludo */}
      <div className="mb-6 anim-aparecer">
        <p className="text-sm first-letter:uppercase texto-tenue">
          {fechaLarga(new Date().toISOString())}
        </p>
        <h2 className="mt-1 text-[28px] leading-tight font-bold texto-base sm:text-4xl">
          {saludo}, {usuario.nombre.split(' ')[0]}.
        </h2>
        <p className="mt-1.5 text-[15px] texto-suave">
          {ventasHoy === 0
            ? 'Todavía no hay ventas registradas hoy.'
            : `${ventasHoy} ${ventasHoy === 1 ? 'venta registrada' : 'ventas registradas'} hoy por ${money(hoy.actual)}.`}
        </p>
      </div>

      {/* Lo urgente del día, en dos columnas cuando hay ancho: lo que
          hay que salir a cobrar a la izquierda y lo que hay que reponer
          a la derecha. En el celular se apilan, cobros primero. */}
      {(cobrosVencidos.length > 0 || stockCritico.length > 0) && (
        <div className="mb-5 grid gap-4 lg:grid-cols-2 lg:items-start">
      {/* Cobros vencidos: cada vendedor ve los suyos, el administrador
          todos. No desaparece hasta que alguien lo marque como
          cobrado. */}
      {cobrosVencidos.length > 0 && (
        <Tarjeta
          className="p-4 sm:p-5"
          style={BORDE_ROJO_NEON}
        >
          <div className="mb-3 flex items-center gap-2.5">
            <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-rojo-50 text-rojo-600 dark:bg-rojo-500/15 dark:text-rojo-300">
              <Wallet className="size-[18px]" />
            </span>
            <div>
              <h3 className="text-[15px] font-bold texto-base">Cobros pendientes</h3>
              <p className="text-[12px] texto-suave">
                {cobrosVencidos.length}{' '}
                {cobrosVencidos.length === 1 ? 'venta vencida sin pagar' : 'ventas vencidas sin pagar'}
                {esAdmin && ' · de todo el equipo'}
              </p>
            </div>
          </div>
          {[
            { titulo: 'Empresas', lista: cobrosEmpresas },
            { titulo: 'Particulares', lista: cobrosParticulares },
          ]
            .filter((grupo) => grupo.lista.length > 0)
            .map((grupo) => (
              <div key={grupo.titulo} className="mb-3 last:mb-0">
                <p className="mb-1.5 text-[11px] font-bold tracking-wide texto-suave uppercase">
                  {grupo.titulo} <span className="texto-tenue">({grupo.lista.length})</span>
                </p>
                {/* Dos por fila apenas hay algo de ancho: son fichas
                    cortas y una sola por línea desperdiciaba media
                    pantalla. */}
                <ul className="grid gap-1.5 sm:grid-cols-2">
                  {grupo.lista.map((v) => {
                    const cliente = datos.clientes.find((c) => c.id === v.clienteId)
                    const telefono = cliente?.telefono?.replace(/[^\d+]/g, '')
                    const vendedor = datos.usuarios.find((u) => u.id === v.usuarioId)
                    const telefonoVendedor = vendedor?.telefono?.replace(/[^\d+]/g, '')
                    return (
                      <li key={v.id} className="relative rounded-xl superficie-hundida p-2.5 pt-2">
                        {/* Cobrado: en la esquina superior derecha, la
                            única acción positiva del recuadro —por eso
                            va aparte, en verde neón, no mezclada abajo
                            con los botones de contacto. */}
                        <button
                          type="button"
                          onClick={async () => {
                            await marcarPagada(v.id, usuario)
                            avisar(`${v.folio} marcada como cobrada`)
                          }}
                          className="absolute top-2 right-2 rounded-full px-2.5 py-1 text-[11px] font-bold text-white transition-transform active:scale-95"
                          style={FONDO_VERDE_NEON}
                        >
                          Cobrado
                        </button>

                        {/* Nombre + WhatsApp del cliente. */}
                        <div className="flex items-center gap-1.5 pr-16">
                          <Link
                            to={`/ventas/${v.id}`}
                            className="truncate text-[13px] font-bold texto-base hover:underline"
                          >
                            {v.clienteNombre}
                          </Link>
                          {telefono && (
                            <a
                              href={`https://wa.me/${telefono.replace('+', '')}?text=${encodeURIComponent(
                                `Hola ${cliente?.nombre}, te escribimos de NovaBerry por el pago de ${v.folio} por ${money(v.total)}. ¡Gracias!`,
                              )}`}
                              target="_blank"
                              rel="noreferrer"
                              aria-label="Recordar por WhatsApp"
                              title="Recordar por WhatsApp"
                              className="shrink-0"
                            >
                              <span
                                className="grid size-6 place-items-center rounded-full transition-transform active:scale-95"
                                style={{ backgroundColor: '#25D366', color: '#fff' }}
                              >
                                <IconoWhatsApp className="size-3" />
                              </span>
                            </a>
                          )}
                        </div>

                        {/* Cuánto lleva sin pagar y cuánto es. Van en la
                            misma línea: con dos fichas por fila el
                            espacio vertical es lo escaso, y las dos
                            cifras se leen juntas de todos modos. */}
                        <div className="mt-0.5 flex items-baseline justify-between gap-2">
                          <p className="min-w-0 truncate text-[11px] font-semibold text-rojo-500">
                            {v.folio} · venció {relativo(v.vencimientoPago)}
                          </p>
                          <span className="cifra shrink-0 text-[14px] font-bold texto-base">
                            {money(v.total)}
                          </span>
                        </div>

                        {/* Línea que separa los datos de la deuda de
                            quién la vendió. "borde-suave" queda del mismo
                            color que el fondo hundido de esta tarjeta y
                            no se alcanza a ver —por eso "borde-base",
                            un tono más marcado. */}
                        <div className="mt-2 border-t-2 borde-base" />

                        {/* Vendedor, con WhatsApp y llamada —para
                            pedirle que ayude a gestionar el cobro. */}
                        <div className="mt-2 flex items-center justify-between gap-2">
                          <p className="min-w-0 truncate text-[11px] texto-suave">
                            Vendido por: <span className="font-semibold texto-base">{v.usuarioNombre}</span>
                          </p>
                          <div className="flex shrink-0 items-center gap-1.5">
                            {telefonoVendedor && (
                              <a
                                href={`https://wa.me/${telefonoVendedor.replace('+', '')}?text=${encodeURIComponent(
                                  `Hola ${vendedor?.nombre}, tu cliente ${v.clienteNombre} tiene pendiente el pago de ${v.folio} por ${money(v.total)}. ¿Puedes ayudarnos a gestionarlo?`,
                                )}`}
                                target="_blank"
                                rel="noreferrer"
                                aria-label={`Escribirle a ${vendedor?.nombre} por WhatsApp`}
                                title={`Escribirle a ${vendedor?.nombre} por WhatsApp`}
                              >
                                <span
                                  className="grid size-7 place-items-center rounded-full transition-transform active:scale-95"
                                  style={{ ...ESTILO_WHATSAPP, backgroundColor: '#25D366', color: '#fff' }}
                                >
                                  <IconoWhatsApp className="size-3.5" />
                                </span>
                              </a>
                            )}
                            {telefonoVendedor && (
                              <a
                                href={`tel:${telefonoVendedor}`}
                                aria-label={`Llamar a ${vendedor?.nombre}`}
                                title={`Llamar a ${vendedor?.nombre}`}
                                className="grid size-7 place-items-center rounded-full border borde-base texto-suave transition-transform active:scale-95"
                              >
                                <Phone className="size-3.5" />
                              </a>
                            )}
                          </div>
                        </div>
                      </li>
                    )
                  })}
                </ul>
              </div>
            ))}
        </Tarjeta>
      )}

      {/* Stock bajo o agotado en el catálogo propio de NovaBerry —no de
          un local de POS, ese es el aviso de más abajo—. Va a la
          derecha de los cobros: las dos son cosas que hay que resolver
          hoy, y juntas se leen de una sola pasada. */}
      {stockCritico.length > 0 && (
        <Tarjeta
          className="p-4 sm:p-5"
          style={stockCritico.some((x) => x.estado === 'agotado') ? BORDE_ROJO_NEON : BORDE_ORO_NEON}
        >
          <div className="mb-3 flex items-center gap-2.5">
            <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-ambar-50 text-ambar-600 dark:bg-ambar-500/15 dark:text-ambar-300">
              <Box className="size-[18px]" />
            </span>
            <div>
              <h3 className="text-[15px] font-bold texto-base">Poco stock en el catálogo</h3>
              <p className="text-[12px] texto-suave">
                {stockCritico.length}{' '}
                {stockCritico.length === 1 ? 'producto necesita reposición' : 'productos necesitan reposición'}
              </p>
            </div>
          </div>
          <ul className="space-y-2">
            {stockCritico.slice(0, 6).map(({ producto, estado }) => (
              <li key={producto.id}>
                <Link
                  to={`/inventario/${producto.id}`}
                  className="flex items-center justify-between gap-3 rounded-2xl superficie-hundida p-3.5 transition-colors hover:opacity-80"
                >
                  <div className="min-w-0">
                    <p className="truncate text-[14px] font-bold texto-base">{producto.nombre}</p>
                    <p className="mt-0.5 text-[12px] texto-suave">{producto.codigo}</p>
                  </div>
                  <span
                    className={cn(
                      'shrink-0 rounded-full px-2.5 py-1 text-[11px] font-bold',
                      ESTADO_STOCK_META[estado].chip,
                    )}
                  >
                    {estado === 'agotado' ? 'Sin stock' : `quedan ${num(producto.stock - producto.reservado)}`}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
          {stockCritico.length > 6 && (
            <Link
              to="/inventario"
              className="mt-3 block text-center text-[13px] font-semibold text-bosque-500 hover:underline dark:text-bosque-200"
            >
              Ver los {stockCritico.length} en Inventario
            </Link>
          )}
        </Tarjeta>
      )}
        </div>
      )}

      {/* Seguimiento pendiente: clientes que ya deberían haber vuelto a
          pedir según su frecuencia habitual. */}
      {seguimientos.length > 0 && (
        <Tarjeta className="mb-5 p-4 sm:p-5" style={BORDE_ORO_NEON}>
          <div className="mb-3 flex items-center gap-2.5">
            <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-[#e8b400]/10 text-[#a87c00] dark:text-[#f0c94d]">
              <Users className="size-[18px]" />
            </span>
            <div>
              <h3 className="text-[15px] font-bold texto-base">Volver a ofrecer</h3>
              <p className="text-[12px] texto-suave">
                {seguimientos.length}{' '}
                {seguimientos.length === 1
                  ? 'cliente ya debería haber pedido de nuevo'
                  : 'clientes ya deberían haber pedido de nuevo'}
              </p>
            </div>
          </div>
          <ul className="space-y-2">
            {seguimientos.map((s) => (
              <li key={s.clienteId}>
                <Link
                  to={`/clientes/${s.clienteId}`}
                  className="flex items-center justify-between gap-3 rounded-2xl superficie-hundida p-3.5 transition-colors hover:opacity-80"
                >
                  <div className="min-w-0">
                    <p className="truncate text-[14px] font-bold texto-base">{s.clienteNombre}</p>
                    <p className="mt-0.5 text-[12px] texto-suave">
                      Pide cada {s.frecuenciaPedidoDias} días · última venta {fecha(s.ultimaVenta)}
                    </p>
                  </div>
                  <span className="shrink-0 rounded-full bg-[#e8b400]/15 px-2.5 py-1 text-[11px] font-bold text-[#a87c00] dark:text-[#f0c94d]">
                    {s.diasDesde} días
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </Tarjeta>
      )}

      {/* Facturas pendientes: una venta a empresa que todavía no se
          facturó. Trae todos los datos que hacen falta para hacerla
          (razón social, RUT, dirección, líneas) para no obligar al
          administrador a ir a buscarlos a otra pantalla. */}
      {facturasPendientes.length > 0 && (
        <Tarjeta className="mb-5 p-4 sm:p-5" style={BORDE_ROJO_NEON}>
          <div className="mb-3 flex items-center gap-2.5">
            <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-rojo-50 text-rojo-600 dark:bg-rojo-500/15 dark:text-rojo-300">
              <Receipt className="size-[18px]" />
            </span>
            <div>
              <h3 className="text-[15px] font-bold texto-base">Falta facturar</h3>
              <p className="text-[12px] texto-suave">
                {facturasPendientes.length}{' '}
                {facturasPendientes.length === 1
                  ? 'venta a empresa sin factura'
                  : 'ventas a empresa sin factura'}
              </p>
            </div>
          </div>
          <ul className="space-y-2.5">
            {facturasPendientes.map((v) => {
              const cliente = datos.clientes.find((c) => c.id === v.clienteId)
              return (
                <FilaFactura
                  key={v.id}
                  venta={v}
                  cliente={cliente}
                  onConfirmar={async (numero) => {
                    await marcarFacturada(v.id, numero, usuario)
                    avisar(`${v.folio} marcada como facturada`)
                  }}
                />
              )
            })}
          </ul>
        </Tarjeta>
      )}

      {/* Fichas de cliente incompletas: nombre, dirección y teléfono
          bastan para vender, pero el resto (comuna, contacto, correo)
          se completa después —esto es el recordatorio de que quedó
          pendiente. */}
      {clientesIncompletos.length > 0 && (
        <Tarjeta className="mb-5 p-4 sm:p-5" style={BORDE_ROJO_NEON}>
          <div className="mb-3 flex items-center gap-2.5">
            <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-rojo-50 text-rojo-600 dark:bg-rojo-500/15 dark:text-rojo-300">
              <UserCog className="size-[18px]" />
            </span>
            <div>
              <h3 className="text-[15px] font-bold texto-base">Faltan agregar datos del cliente</h3>
              <p className="text-[12px] texto-suave">
                {clientesIncompletos.length}{' '}
                {clientesIncompletos.length === 1 ? 'ficha incompleta' : 'fichas incompletas'}
              </p>
            </div>
          </div>
          <ul className="space-y-2">
            {clientesIncompletos.map(({ cliente, faltan }) => (
              <li key={cliente.id}>
                <Link
                  to={`/clientes/${cliente.id}`}
                  className="flex items-center justify-between gap-3 rounded-2xl superficie-hundida p-3.5 transition-colors hover:opacity-80"
                >
                  <div className="min-w-0">
                    <p className="truncate text-[14px] font-bold texto-base">
                      {[cliente.nombre, cliente.apellido].filter(Boolean).join(' ')}
                    </p>
                    <p className="mt-0.5 truncate text-[12px] text-rojo-500 font-semibold">
                      Falta: {faltan.join(', ')}
                    </p>
                  </div>
                  <ChevronRight className="size-4 shrink-0 texto-tenue" />
                </Link>
              </li>
            ))}
          </ul>
        </Tarjeta>
      )}

      {/* A un local le está quedando poco de algo: hay que mandarle
          más. El mínimo lo define cada producto en su local, y es el
          mismo que hace saltar el aviso allá en el POS. */}
      {faltantes.length > 0 && (
        <Tarjeta className="mb-5 p-4 sm:p-5" style={BORDE_ROJO_NEON}>
          <div className="mb-3 flex items-center gap-2.5">
            <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-rojo-50 text-rojo-600 dark:bg-rojo-500/15 dark:text-rojo-300">
              <Box className="size-[18px]" />
            </span>
            <div>
              <h3 className="text-[15px] font-bold texto-base">Poco stock en un local</h3>
              <p className="text-[12px] texto-suave">
                {faltantes.length}{' '}
                {faltantes.length === 1 ? 'producto bajo el mínimo' : 'productos bajo el mínimo'} —
                hay que reponer
              </p>
            </div>
          </div>
          <ul className="space-y-2">
            {faltantes.map((f) => (
              <li key={f.id}>
                <Link
                  to={`/sucursales/${f.sucursalId}`}
                  className="flex items-center justify-between gap-3 rounded-2xl superficie-hundida p-3.5 transition-colors hover:opacity-80"
                >
                  <div className="min-w-0">
                    <p className="truncate text-[14px] font-bold texto-base">{f.producto}</p>
                    <p className="mt-0.5 text-[12px] texto-suave">{f.sucursal}</p>
                  </div>
                  <span className="shrink-0 rounded-full bg-rojo-50 px-2.5 py-1 text-[11px] font-bold text-rojo-600 dark:bg-rojo-500/15 dark:text-rojo-300">
                    quedan {num(f.cantidad)}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </Tarjeta>
      )}

      {/* Tarjetas principales */}
      {esAdmin ? (
        <div className="mb-6 grid grid-cols-2 gap-3 escalonar lg:grid-cols-3">
          <TarjetaCifra
            etiqueta="Ventas del día"
            valor={money(hoy.actual)}
            delta={hoy.delta}
            pie={`${ventasHoy} ${ventasHoy === 1 ? 'venta' : 'ventas'}`}
            icono={Receipt}
            tono="bosque"
            onClick={() => navegar('/ventas')}
          />
          <TarjetaCifra
            etiqueta="Ventas de la semana"
            valor={money(semana.actual)}
            delta={semana.delta}
            pie="vs. semana anterior"
            icono={ArrowUpRight}
            tono="lima"
            onClick={() => navegar('/ventas')}
          />
          <TarjetaCifra
            etiqueta="Ventas del mes"
            valor={money(mes.actual)}
            delta={mes.delta}
            pie="vs. mes anterior"
            icono={ArrowUpRight}
            tono="magenta"
            onClick={() => navegar('/ventas')}
          />
          <TarjetaCifra
            etiqueta="Stock disponible"
            valor={num(inv.unidades)}
            pie={`${inv.productos} productos · ${moneyCorto(inv.valorVenta)}`}
            icono={Box}
            tono="tinta"
            onClick={() => navegar('/inventario')}
          />
          <TarjetaCifra
            etiqueta="Clientes"
            valor={num(datos.clientes.length)}
            pie={`${datos.clientes.filter((c) => c.tipo === 'empresa').length} empresas`}
            icono={Users}
            tono="bosque"
            onClick={() => navegar('/clientes')}
          />
          <TarjetaCifra
            etiqueta="Pedidos pendientes"
            valor={num(pendientes.length)}
            pie={pendientes.length ? moneyCorto(suma(pendientes)) : 'Todo entregado'}
            icono={Truck}
            tono="rojo"
            onClick={() => navegar('/pedidos')}
          />
        </div>
      ) : (
        <div className="mb-6 escalonar">
          <Tarjeta className="mb-3 divide-y borde-suave overflow-hidden p-0">
            <FilaVenta
              etiqueta="Ventas del día"
              valor={money(hoy.actual)}
              delta={hoy.delta}
              pie={`${ventasHoy} ${ventasHoy === 1 ? 'venta' : 'ventas'}`}
              icono={Receipt}
              tono="bosque"
              onClick={() => navegar('/ventas')}
            />
            <FilaVenta
              etiqueta="Ventas de la semana"
              valor={money(semana.actual)}
              delta={semana.delta}
              pie="vs. semana anterior"
              icono={ArrowUpRight}
              tono="lima"
              onClick={() => navegar('/ventas')}
            />
            <FilaVenta
              etiqueta="Ventas del mes"
              valor={money(mes.actual)}
              delta={mes.delta}
              pie="vs. mes anterior"
              icono={ArrowUpRight}
              tono="magenta"
              onClick={() => navegar('/ventas')}
            />
          </Tarjeta>
          <TarjetaPedidos
            valor={num(pendientes.length)}
            pie={pendientes.length ? moneyCorto(suma(pendientes)) : 'Todo entregado'}
            onClick={() => navegar('/pedidos')}
          />
        </div>
      )}

      {/* Tendencia */}
      <Tarjeta className="p-5">
        <div className="mb-4 flex items-end justify-between gap-3">
          <div>
            <h3 className="text-base font-bold texto-base">Últimos 14 días</h3>
            <p className="mt-0.5 text-sm texto-suave">
              {money(serie.reduce((s, d) => s + d.total, 0))} en total
            </p>
          </div>
          <Link
            to={esAdmin ? '/estadisticas' : '/ventas'}
            className="text-[13px] font-semibold text-bosque-500 hover:underline dark:text-bosque-200"
          >
            Ver detalle
          </Link>
        </div>

        <div className="h-44 -mx-2">
          <Suspense fallback={<div className="size-full rounded-2xl superficie-hundida anim-pulso" />}>
            <GraficoVentas datos={serie} />
          </Suspense>
        </div>
      </Tarjeta>

      {/* Últimas ventas */}
      <Tarjeta className="mt-4 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4">
          <h3 className="text-base font-bold texto-base">Últimas ventas</h3>
          <Link
            to="/ventas"
            className="text-[13px] font-semibold text-bosque-500 hover:underline dark:text-bosque-200"
          >
            Ver todas
          </Link>
        </div>

        {ultimas.length === 0 ? (
          <p className="px-5 pb-8 text-center text-sm texto-tenue">
            Aún no registras ventas. El botón verde de abajo abre una nueva.
          </p>
        ) : (
          <ul className="divide-y borde-suave">
            {ultimas.map((v) => (
              <li key={v.id}>
                <Link
                  to={`/ventas/${v.id}`}
                  className="flex items-center gap-3 px-5 py-3.5 transition-colors hover:superficie-hundida"
                >
                  <Avatar nombre={v.clienteNombre} tamano="sm" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[15px] font-semibold texto-base">{v.clienteNombre}</p>
                    <p className="truncate text-[13px] texto-suave">
                      <span className="codigo">{v.folio}</span> · {hora(v.fecha)} ·{' '}
                      {v.lineas.length} {v.lineas.length === 1 ? 'producto' : 'productos'}
                    </p>
                  </div>
                  <p className="cifra shrink-0 text-[15px] font-bold texto-base">{money(v.total)}</p>
                  <ChevronRight className="size-4 shrink-0 texto-tenue" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Tarjeta>
    </Pagina>
  )
}

const TONOS = {
  bosque: 'bg-bosque-50 text-bosque-600 dark:bg-bosque-500/20 dark:text-bosque-200',
  lima: 'bg-lima-50 text-lima-700 dark:bg-lima-500/20 dark:text-lima-200',
  magenta: 'bg-magenta-50 text-magenta-600 dark:bg-magenta-500/20 dark:text-magenta-200',
  tinta: 'bg-tinta-50 text-tinta-600 dark:bg-tinta-500/20 dark:text-tinta-200',
  rojo: 'bg-rojo-50 text-rojo-600 dark:bg-rojo-500/20 dark:text-rojo-200',
}

/** Fila dentro de la tarjeta de ventas del vendedor: día, semana y mes
 *  juntos en un solo bloque prolijo, en vez de tres tarjetas sueltas. */
function FilaVenta({
  etiqueta,
  valor,
  pie,
  delta,
  icono: Icono,
  tono,
  onClick,
}: {
  etiqueta: string
  valor: string
  pie?: string
  delta?: number
  icono: typeof Box
  tono: keyof typeof TONOS
  onClick?: () => void
}) {
  const sube = (delta ?? 0) >= 0
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors hover:superficie-hundida sm:px-5"
    >
      <span className={cn('grid size-9 shrink-0 place-items-center rounded-xl', TONOS[tono])}>
        <Icono className="size-[18px]" strokeWidth={2.4} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-semibold texto-suave">{etiqueta}</p>
        {pie && <p className="truncate text-[12px] texto-tenue">{pie}</p>}
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1">
        <p className="cifra font-display text-[17px] leading-tight font-bold texto-base">{valor}</p>
        {delta !== undefined && (
          <span
            className={cn(
              'flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-bold',
              sube
                ? 'bg-lima-50 text-lima-700 dark:bg-lima-500/15 dark:text-lima-300'
                : 'bg-rojo-50 text-rojo-600 dark:bg-rojo-500/15 dark:text-rojo-300',
            )}
          >
            {sube ? <ArrowUpRight className="size-2.5" /> : <ArrowDownRight className="size-2.5" />}
            {pct(Math.abs(delta))}
          </span>
        )}
      </div>
    </button>
  )
}

/** Tarjeta de "Pedidos pendientes" del vendedor: el camión va al lado
 *  del título, y el número grande queda debajo —así se lee de un
 *  vistazo, sin el ícono separado arriba como en TarjetaCifra. */
function TarjetaPedidos({
  valor,
  pie,
  onClick,
}: {
  valor: string
  pie?: string
  onClick?: () => void
}) {
  return (
    <Tarjeta onClick={onClick} className="p-4 sm:p-5">
      <div className="flex items-center justify-between gap-2.5">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className={cn('grid size-9 shrink-0 place-items-center rounded-xl', TONOS.rojo)}>
            <Truck className="size-[18px]" strokeWidth={2.4} />
          </span>
          <div className="min-w-0">
            <p className="truncate text-[13px] font-semibold texto-suave">Pedidos pendientes</p>
            {pie && <p className="truncate text-[12px] texto-tenue">{pie}</p>}
          </div>
        </div>
        <p className="cifra shrink-0 font-display text-[26px] leading-tight font-bold texto-base sm:text-3xl">
          {valor}
        </p>
      </div>
    </Tarjeta>
  )
}

function TarjetaCifra({
  etiqueta,
  valor,
  pie,
  delta,
  icono: Icono,
  tono,
  onClick,
}: {
  etiqueta: string
  valor: string
  pie?: string
  delta?: number
  icono: typeof Box
  tono: keyof typeof TONOS
  onClick?: () => void
}) {
  const sube = (delta ?? 0) >= 0
  return (
    <Tarjeta onClick={onClick} className="p-4 sm:p-5">
      <div className="mb-3 flex items-start justify-between gap-2">
        <span className={cn('grid size-9 place-items-center rounded-xl', TONOS[tono])}>
          <Icono className="size-[18px]" strokeWidth={2.4} />
        </span>
        {delta !== undefined && (
          <span
            className={cn(
              'flex items-center gap-0.5 rounded-full px-2 py-1 text-[11px] font-bold',
              sube
                ? 'bg-lima-50 text-lima-700 dark:bg-lima-500/15 dark:text-lima-300'
                : 'bg-rojo-50 text-rojo-600 dark:bg-rojo-500/15 dark:text-rojo-300',
            )}
          >
            {sube ? <ArrowUpRight className="size-3" /> : <ArrowDownRight className="size-3" />}
            {pct(Math.abs(delta))}
          </span>
        )}
      </div>
      <p className="text-[13px] font-semibold texto-suave">{etiqueta}</p>
      <p className="cifra mt-0.5 font-display text-[26px] leading-tight font-bold texto-base sm:text-3xl">
        {valor}
      </p>
      {pie && <p className="mt-1 truncate text-[12px] texto-tenue">{pie}</p>}
    </Tarjeta>
  )
}

/* ------------------------- Factura pendiente ------------------------- */

/** Una venta a empresa sin facturar, con todo lo que hace falta para
 *  emitirla (razón social, RUT, dirección, líneas) y un campo para
 *  anotar el número una vez hecha en el sistema de facturación de
 *  verdad —esta app no factura, solo lleva la cuenta de qué falta—. */
function FilaFactura({
  venta,
  cliente,
  onConfirmar,
}: {
  venta: Venta
  cliente: Cliente | undefined
  onConfirmar: (numero: string) => Promise<void>
}) {
  const [numero, setNumero] = useState('')
  const [enviando, setEnviando] = useState(false)

  const confirmar = async () => {
    setEnviando(true)
    try {
      await onConfirmar(numero)
    } finally {
      setEnviando(false)
    }
  }

  return (
    <li className="rounded-2xl superficie-hundida p-3.5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <Link
            to={`/ventas/${venta.id}`}
            className="block truncate text-[14px] font-bold texto-base hover:underline"
          >
            {venta.clienteNombre}
          </Link>
          <p className="mt-0.5 text-[12px] texto-suave">
            {venta.folio} · {fecha(venta.fecha)}
          </p>
        </div>
        <span className="cifra shrink-0 text-[15px] font-bold texto-base">{money(venta.total)}</span>
      </div>

      {/* Todo lo que hace falta para emitirla, junto y sin tener que ir
          a buscarlo a la ficha del cliente. */}
      <div className="mt-2.5 grid grid-cols-2 gap-x-3 gap-y-1 rounded-xl border borde-suave p-2.5 text-[12px]">
        <p className="truncate texto-suave">
          RUT: <span className="font-semibold texto-base">{cliente?.rut || 'Sin RUT'}</span>
        </p>
        <p className="truncate texto-suave">
          Rubro: <span className="font-semibold texto-base">{cliente?.rubro || '—'}</span>
        </p>
        <p className="col-span-2 truncate texto-suave">
          Dirección:{' '}
          <span className="font-semibold texto-base">
            {[cliente?.direccion, cliente?.comuna].filter(Boolean).join(', ') || 'Sin dirección'}
          </span>
        </p>
      </div>

      {/* Las líneas de la venta: lo que va detallado en la factura. */}
      <ul className="mt-2 space-y-0.5">
        {venta.lineas.map((l) => (
          <li key={l.productoId} className="flex items-center justify-between gap-2 text-[12px]">
            <span className="min-w-0 truncate texto-suave">
              {l.cantidad} × {l.nombre}
            </span>
            <span className="cifra shrink-0 texto-suave">{money(l.subtotal)}</span>
          </li>
        ))}
      </ul>

      {/* Anotar el número de factura es opcional —lo importante es
          marcarla como hecha—, pero si se escribe queda guardado para
          poder encontrarla después. */}
      <div className="mt-3 flex items-center gap-2 border-t borde-suave pt-3">
        <Entrada
          value={numero}
          onChange={(e) => setNumero(e.target.value)}
          placeholder="N° de factura (opcional)"
          className="h-9 flex-1 text-[13px]"
        />
        <Boton
          tamano="sm"
          cargando={enviando}
          icono={<Check className="size-3.5" />}
          onClick={confirmar}
        >
          Facturada
        </Boton>
      </div>
    </li>
  )
}
